import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { LanguageId, MentorAvailability } from '@interfaces';
import { FuncService } from '../../../../../../services';
import { PersonFinderWidget } from '../../../../search';

export interface AddMentorDialogContextData {
	accTeamId: number,
	teamName: string | undefined,
	trimesterId: number,
	accId: number,
	languageId: number,
}

@Component({
	selector: 'add-mentor-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		PersonFinderWidget,
		NgClass,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './add-mentor.dialog.html'
})

export class AddMentorDialog {

	public dialogRef = inject(MatDialogRef<AddMentorDialog>);
	public dialogContext: DialogContext<AddMentorDialogContextData> = inject(MAT_DIALOG_DATA);
	private func = inject(FuncService);

	public selectedPersonId: number | undefined = undefined;
	public mentorAvailabilityCheck = signal<MentorAvailability | undefined>(undefined);

	public actions = computed<DialogAction[]>(() => {
		const actions: DialogAction[] = [];

		if (!this.dialogContext.data) return actions;

		const mentorValidation = this.mentorValidation();

		if (!mentorValidation) {
			actions.push({
				id: 'check',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Check'),
				linkType: 'callback',
				callback: async () => await this.checkMentorAvailability(),
				willCloseDialog: false,
			});
		} else if (mentorValidation.canAdd) {
			actions.push({
				id: 'add',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Add'),
				linkType: 'callback',
				callback: async () => await this.addMentor(),
				willCloseDialog: true,
			});
		}

		return [
			...actions,
			{
				id: 'close',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Close'),
				linkType: "no-action",
				willCloseDialog: true,
			}
		]
	});

	setSelectedPersonId(personId: number) {
		this.selectedPersonId = personId;
		this.mentorAvailabilityCheck.set(undefined);
	}

	async checkMentorAvailability() {
		const trimesterId = this.dialogContext.data?.trimesterId;
		if (!this.selectedPersonId || !trimesterId) return;
		const data = await this.func.accTeam.isMentorAvailable({ personId: this.selectedPersonId, trimesterId });
		this.mentorAvailabilityCheck.set(data);
	}

	async addMentor() {
		const data = this.dialogContext.data;
		const volunteerDetails = this.mentorAvailabilityCheck()?.volunteerDetails;
		if (!this.selectedPersonId || !data || !volunteerDetails) return;

		await this.func.accTeam.addMentor({
			accTeamId: data.accTeamId,
			accId: data.accId,
			personId: this.selectedPersonId,
			volunteerId: volunteerDetails.volunteerId
		});
	}

	isProgramLanguageApproved(canVolunteerInEnglish: boolean | undefined, canVolunteerInSpanish: boolean | undefined) {
		const languageId = this.dialogContext.data?.languageId;
		if (!languageId) return false;

		return languageId === LanguageId.English ? canVolunteerInEnglish : canVolunteerInSpanish;
	}

	// Can add only if the person is a volunteer, approved as mentor and can volunteer in the program language,	and not already in the current team
	public mentorValidation = computed<{ canAdd: boolean, isWarning: boolean, text: string, subText?: string } | undefined>(() => {
		const mentor = this.mentorAvailabilityCheck();
		if (!mentor) return undefined;

		const teamName = this.dialogContext.data?.teamName;
		const volunteer = mentor.volunteerDetails;

		// Not a volunteer
		if (!volunteer) {
			return {
				canAdd: false,
				isWarning: true,
				text: 'Cannot Add. This person is not registered as a volunteer.'
			};
		}

		//check if mentor is approved and can volunteer in program language
		const isProgramLanguageApproved = this.isProgramLanguageApproved(volunteer.canVolunteerInEnglish, volunteer.canVolunteerInSpanish);
		if (!volunteer.isMentorApproved || !isProgramLanguageApproved) {

			const reasons: string[] = [];

			if (!volunteer.isMentorApproved) {
				reasons.push('not approved as a mentor');
			}

			if (!isProgramLanguageApproved) {
				reasons.push('not approved to mentor in the program language');
			}

			return {
				canAdd: false,
				isWarning: true,
				text: 'Cannot Add. This person is ' + reasons.join(' and ') + '.'
			};
		}


		// Check existing teams
		if (mentor.existingTeams && mentor.existingTeams.length > 0) {
			// Already in current team
			if (mentor.existingTeams.map(et => et.accTeamId).includes(this.dialogContext.data?.accTeamId ?? 0)) {
				return {
					canAdd: false,
					isWarning: false,
					text: `This person is already a mentor in the ${teamName} team.`
				};
			}

			// Already in other teams
			const existingTeamNames = mentor.existingTeams.map(t => t.name).join(', ');
			return {
				canAdd: true,
				isWarning: true,
				text: `This person is already associated with team(s): ${existingTeamNames}, for this trimester.`,
				subText: `Do you wish to continue adding them to the ${teamName} team?`
			};
		}

		// All validations passed
		return {
			canAdd: true,
			isWarning: false,
			text: `Looks good! Continue adding this person to ${teamName} team?`
		};
	});

}